/* eslint-disable camelcase */
import { LinkText } from '@postscript/components';
import { useQuery } from '@tanstack/react-query';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { api } from 'controllers/network/apiClient';
import { useEffect } from 'react';
import { BANNER_TYPES } from 'utils/events';

const SHOPIFY_APPS_PERMISSIONS_LINK =
  'https://help.shopify.com/en/manual/your-account/staff-accounts/staff-permissions/staff-permissions-descriptions#apps-and-channels-permissions';

type Severity = 'critical' | 'degraded' | 'optional';

const fetchScopes = () =>
  api.get<{ permission_url?: string; severity?: string }>(
    '/v2/platforms/shopify/scopes/upgrade_url',
  );

const permissionLink = (
  <LinkText href={SHOPIFY_APPS_PERMISSIONS_LINK} target="_blank">
    Manage and install apps and channels
  </LinkText>
);

// The backend classifies the gap: `critical` means every core scope is absent,
// `degraded` means some core scope is, `optional` means only non-core scopes
// are. Only the first is an outage from the merchant's point of view, so only it
// gets urgent language.
const BANNER_BY_SEVERITY = {
  critical: {
    variant: 'critical',
    heading: 'Postscript has lost access to your Shopify store',
    bodyText: (
      <>
        Postscript can no longer read your products or manage your discounts.
        Messages may send without product images, and discount codes may not be
        delivered, until you reconnect. You&apos;ll need the {permissionLink}{' '}
        permission.
      </>
    ),
  },
  degraded: {
    variant: 'default',
    heading: 'Some Shopify permissions are missing',
    bodyText: (
      <>
        Reconnecting Shopify lets Postscript use everything your plan includes.
        To make this change, you&apos;ll need the {permissionLink} permission.
      </>
    ),
  },
  optional: {
    variant: 'default',
    heading: 'Additional Shopify permissions required',
    bodyText: (
      <>
        New Postscript features will require additional Shopify access. To make
        this change, you&apos;ll need the {permissionLink} permission.
      </>
    ),
  },
} as const;

const isSeverity = (value: unknown): value is Severity =>
  typeof value === 'string' && value in BANNER_BY_SEVERITY;

interface ScopesUpdateBannerProps {
  isEntitled: boolean;
}

const ScopesUpdateBanner = ({ isEntitled }: ScopesUpdateBannerProps) => {
  const { addBanner, removeBanner } = useBanners();
  const { data } = useQuery({
    queryKey: ['scopes'],
    queryFn: fetchScopes,
    // This now runs for every shop rather than only entitled ones, and each
    // call reaches Shopify, so refetch on focus but honour staleTime rather
    // than firing on every focus in every open tab.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const { permission_url } = data || {};
  const rawSeverity = data?.severity;
  const severity: Severity = isSeverity(rawSeverity) ? rawSeverity : 'optional';

  useEffect(() => {
    // Only a total loss of access is urgent enough to interrupt every merchant.
    // `degraded` shops are usually older installs that predate later additions
    // to the core scope set and are working fine, so they keep the previous
    // behaviour -- prompted only when entitled -- until that population is
    // sized.
    const shouldShow =
      Boolean(permission_url) && (severity === 'critical' || isEntitled);

    if (!shouldShow) {
      // A shop that reconnects stops reporting a permission_url, and without
      // this the warning would sit there, undismissable, until a full reload.
      removeBanner('SCOPES_UPDATE_BANNER');
      return undefined;
    }

    addBanner({
      id: 'SCOPES_UPDATE_BANNER',
      // Belongs at the top level, not inside `data`: EmergencyBanner reads
      // banner.announcementType to decide what is pinned to the top of the
      // app, and NotificationsPanel routes everything else to the bell. Nested
      // one level down it is invisible to both, and a total loss of Shopify
      // access shows up as nothing more than a bump on the bell badge.
      // Only critical is escalated; the other severities keep the placement
      // they have today.
      ...(severity === 'critical'
        ? { announcementType: AnnouncementType.EMERGENCY }
        : {}),
      data: {
        ...BANNER_BY_SEVERITY[severity],
        primaryAction: {
          onClick: () => window.open(permission_url, '_self'),
          text: 'Go to Shopify',
        },
      },
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.SHOPIFY_SCOPES_UPDATE,
      },
    });

    // ADD_BANNER prepends without de-duplicating by id, so a severity change
    // would leave two entries. EmergencyBanner filters the whole list for
    // EMERGENCY before taking one, so the stale critical entry would outlive
    // the newer degraded one and keep telling a shop that has partly
    // reconnected that it has lost all access.
    return () => removeBanner('SCOPES_UPDATE_BANNER');
  }, [permission_url, severity, isEntitled]);

  return null;
};

export default ScopesUpdateBanner;
